import type { AgGridReact } from 'ag-grid-react';
import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Hook to fit AG Grid columns when container width stabilizes or changes.
 * Uses requestAnimationFrame-based polling of container width to detect stability.
 *
 * Usage:
 *   const gridRef = useRef<AgGridReact<RowType>>(null);
 *   const containerRef = useRef<HTMLDivElement>(null);
 *   useAgGridFitWhenStable(gridRef, containerRef, [rowData]);
 *   <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
 *     <Table ref={gridRef} ... />
 *   </div>
 *
 * @param gridRef - React ref to the AgGridReact instance
 * @param containerRef - React ref to the grid's container element
 * @param deps - Memoized array of dependencies (required, e.g., useMemo(() => [rowData], [rowData]))
 *   **You must memoize this array to avoid unnecessary effect restarts and to satisfy React Hook lint rules.**
 *   The deps array itself is included as a single dependency; do not pass a new array each render.
 * @param stableFrames - Number of frames width must be stable (default 5)
 * @param maxFrames - Max frames to check before fallback fit (default 60)
 */
export function useAgGridFitWhenStable<RowType>(
  gridRef: RefObject<AgGridReact<RowType> | null>,
  containerRef: RefObject<HTMLDivElement | null>,
  deps: unknown[],
  stableFrames = 5,
  maxFrames = 60,
) {
  useEffect(() => {
    // SSR/Non-browser guard
    if (typeof window === 'undefined') return;
    if (!containerRef.current || !gridRef.current) return;

    let lastWidth = containerRef.current.offsetWidth;
    let stableCount = 0;
    let frameCount = 0;
    let rafId: number;

    const checkWidth = () => {
      const width = containerRef.current?.offsetWidth || 0;
      if (width === lastWidth && width > 0) {
        stableCount++;
      } else {
        stableCount = 0;
        lastWidth = width;
      }
      frameCount++;

      if (stableCount >= stableFrames && gridRef.current?.api) {
        gridRef.current.api.sizeColumnsToFit();
        return;
      }

      if (frameCount < maxFrames) {
        rafId = window.requestAnimationFrame(checkWidth);
      } else if (gridRef.current?.api) {
        // Fallback: fit columns even if not stable after maxFrames
        gridRef.current.api.sizeColumnsToFit();
      }
    };

    rafId = window.requestAnimationFrame(checkWidth);

    return () => {
      if (typeof window !== 'undefined') {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [gridRef, containerRef, stableFrames, maxFrames, deps]);
}
